import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, MonitorSmartphone } from 'lucide-react';
import { findCloudPlan, findPartnerTier, type ChangePasswordRequest, type Entitlements, type PortalAppliance, type PortalSubscription, type UsageSummary } from '@scada-ink/shared/cloud';
import { Button } from '@/components/ui';
import { Alert, TextField, Toggle } from '@/components/form';
import { usePageTitle } from '@/components/layout/SiteLayout';
import { cloudApi, errorMessage } from '@/lib/cloudApi';
import { useSession } from '@/lib/session';
import { useAsync } from '@/lib/useAsync';
import { Card, ConnectionBadge, EmptyState, LoadError, Loading, Meter, PortalHeader, StatCard, formatDate } from './components';

// ── AI usage ───────────────────────────────────────────────────────────────

export function AiUsagePage() {
  usePageTitle('AI Usage');
  const { data, error, loading, reload } = useAsync(async () => {
    const [usage, entitlements] = await Promise.all([cloudApi.get<UsageSummary>('/api/portal/usage'), cloudApi.get<Entitlements>('/api/portal/entitlements')]);
    return { usage, entitlements };
  });
  if (loading && !data) return <Loading />;
  if (error || !data) return <LoadError message={error ?? 'Could not load usage.'} onRetry={reload} />;

  const { usage, entitlements } = data;
  const peak = Math.max(1, ...usage.byMonth.map((m) => m.requests + m.pageDesigns));

  return (
    <>
      <PortalHeader
        title="Smart Assist AI"
        description="AI requests from every appliance registered to your account. Usage is attributed by the appliance’s license key."
        actions={
          !entitlements.ai.active ? (
            <Button to="/portal/billing?plan=ai-assist" size="sm">
              <Sparkles size={14} /> Subscribe
            </Button>
          ) : !entitlements.ai.deepAnalysis ? (
            <Button to="/portal/billing?plan=ai-assist-pro" size="sm" variant="secondary">
              Upgrade to Pro
            </Button>
          ) : undefined
        }
      />
      {!entitlements.ai.active && (
        <div className="mb-6">
          <Alert kind="info">No Smart Assist plan is active. Subscribe to use AI tag classification, observation analysis and the page designer.</Alert>
        </div>
      )}
      <div className="grid sm:grid-cols-3 gap-3 mb-6">
        <StatCard label="Requests this month" value={usage.ai.requests.toLocaleString()} sub={`since ${formatDate(usage.periodStart)}`} />
        <StatCard label="Page designs this month" value={usage.ai.pageDesigns.toLocaleString()} />
        <StatCard label="Tags analyzed this month" value={usage.ai.tagsAnalyzed.toLocaleString()} />
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <Card title="Allowance">
          <div className="space-y-5">
            <Meter label="Analysis requests" used={usage.ai.requests} max={usage.ai.included.requests} />
            <Meter label="AI page designs" used={usage.ai.pageDesigns} max={usage.ai.included.pageDesigns} />
            <div className="text-xs text-text-muted">Deep multi-stage analysis: {entitlements.ai.deepAnalysis ? 'included' : 'Smart Assist Pro only'}</div>
          </div>
        </Card>
        <Card title="Last six months">
          <div className="flex items-end gap-3 h-40">
            {usage.byMonth.map((m) => {
              const total = m.requests + m.pageDesigns;
              return (
                <div key={m.month} className="flex-1 flex flex-col items-center justify-end h-full" title={`${m.requests} requests, ${m.pageDesigns} designs`}>
                  <span className="text-[11px] tabular-nums text-text-secondary mb-1">{total}</span>
                  <div className="w-full flex flex-col justify-end rounded-t-md overflow-hidden" style={{ height: `${(total / peak) * 100}%`, minHeight: total ? 4 : 0 }}>
                    <div className="bg-brand-secondary" style={{ flex: m.pageDesigns }} />
                    <div className="bg-brand-primary" style={{ flex: m.requests }} />
                  </div>
                  <span className="mt-1.5 text-[11px] text-text-muted">{new Date(`${m.month}-01T00:00:00`).toLocaleDateString(undefined, { month: 'short' })}</span>
                </div>
              );
            })}
          </div>
          <div className="mt-3 flex gap-4 text-xs text-text-secondary">
            <span className="inline-flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-sm bg-brand-primary" /> Requests
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-sm bg-brand-secondary" /> Page designs
            </span>
          </div>
        </Card>
      </div>
    </>
  );
}

// ── Per-appliance service coverage (archive / remote) ─────────────────────

export function Coverage({ kind, appliances, covered, onChanged }: { kind: 'archive' | 'remote'; appliances: PortalAppliance[]; covered: number; onChanged: () => void }) {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const field = kind === 'archive' ? 'archiveEnabled' : 'remoteEnabled';
  const enabled = appliances.filter((a) => a[field]).length;

  async function toggle(a: PortalAppliance, value: boolean) {
    setBusy(a.id);
    setError(null);
    try {
      await cloudApi.patch(`/api/portal/appliances/${encodeURIComponent(a.id)}`, { [field]: value });
      onChanged();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(null);
    }
  }

  return (
    <Card title="Appliances" actions={<span className="text-xs text-text-secondary">{enabled} of {covered} covered</span>} padded={appliances.length === 0}>
      {appliances.length === 0 ? (
        <EmptyState icon={MonitorSmartphone} title="No appliances registered" body="Register an appliance first." action={<Button to="/portal/appliances?claim=1" size="sm">Add appliance</Button>} />
      ) : (
        <>
          {error && (
            <div className="px-5 pt-4">
              <Alert kind="error">{error}</Alert>
            </div>
          )}
          <ul className="divide-y divide-glass-border">
            {appliances.map((a) => (
              <li key={a.id} className="flex items-center gap-3 px-5 py-3">
                <div className="flex-1 min-w-0">
                  <Link to={`/portal/appliances/${a.id}`} className="text-sm font-semibold hover:text-brand-primary">
                    {a.name}
                  </Link>
                  <div className="text-xs text-text-muted">{a.site || '—'}</div>
                </div>
                <ConnectionBadge status={a.status} />
                <Toggle label="" checked={a[field]} disabled={busy === a.id || (!a[field] && enabled >= covered)} onChange={(v) => void toggle(a, v)} />
              </li>
            ))}
          </ul>
        </>
      )}
    </Card>
  );
}


// ── Account ────────────────────────────────────────────────────────────────

export function AccountPage() {
  usePageTitle('Account');
  const { session } = useSession();
  const [form, setForm] = useState<ChangePasswordRequest & { confirm: string }>({ currentPassword: '', newPassword: '', confirm: '' });
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);
  const subs = useAsync(async () => (session?.user.role === 'viewer' ? [] : cloudApi.get<PortalSubscription[]>('/api/portal/subscriptions')), [session?.user.role]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (form.newPassword !== form.confirm) {
      setNotice({ kind: 'error', text: 'The new passwords do not match.' });
      return;
    }
    if (form.newPassword.length < 10) {
      setNotice({ kind: 'error', text: 'Use a password of at least 10 characters.' });
      return;
    }
    setBusy(true);
    setNotice(null);
    try {
      await cloudApi.post('/api/portal/auth/change-password', { currentPassword: form.currentPassword, newPassword: form.newPassword });
      setForm({ currentPassword: '', newPassword: '', confirm: '' });
      setNotice({ kind: 'success', text: 'Password changed. Other devices have been signed out.' });
    } catch (err) {
      setNotice({ kind: 'error', text: errorMessage(err) });
    } finally {
      setBusy(false);
    }
  }

  if (!session) return null;
  const live = (subs.data ?? []).filter((s) => s.status !== 'canceled');

  return (
    <>
      <PortalHeader title="Account" />
      <div className="grid lg:grid-cols-2 gap-6">
        <Card title="Profile">
          <dl className="grid grid-cols-[120px_1fr] gap-y-3 text-sm">
            <dt className="text-text-muted">Name</dt>
            <dd className="font-medium">{session.user.name}</dd>
            <dt className="text-text-muted">Email</dt>
            <dd className="font-medium">{session.user.email}</dd>
            <dt className="text-text-muted">Company</dt>
            <dd className="font-medium">{session.account.company}</dd>
            <dt className="text-text-muted">Role</dt>
            <dd className="font-medium capitalize">{session.user.role}</dd>
            <dt className="text-text-muted">Member since</dt>
            <dd className="font-medium">{formatDate(session.user.createdAt)}</dd>
            {session.user.role !== 'viewer' && (
              <>
            <dt className="text-text-muted">Billing</dt>
            <dd className="font-medium">{session.billing.provider === 'stripe' ? 'Card via Stripe' : 'Invoice'}</dd>
            <dt className="text-text-muted">Partner</dt>
            <dd className="font-medium">
              {session.account.partnerTier ? (
                <>
                  {findPartnerTier(session.account.partnerTier)?.name} partner · {session.account.partnerDiscountPct}% license discount
                  {session.account.partnerDiscountBlockedReason && <span className="block text-xs font-normal text-status-idle">Discount paused: {session.account.partnerDiscountBlockedReason}</span>}
                </>
              ) : (
                <Link to="/partners" className="text-brand-primary">
                  Become a partner
                </Link>
              )}
            </dd>
            <dt className="text-text-muted">Plans</dt>
            <dd className="font-medium">{live.length ? live.map((s) => findCloudPlan(s.planId)?.name ?? s.planId).join(', ') : 'None'}</dd>
              </>
            )}
          </dl>
          <p className="mt-5 text-xs text-text-muted">
            To change your name, email or company, <Link to="/contact" className="text-brand-primary font-semibold">contact support</Link>.
          </p>
        </Card>
        <Card title="Change password">
          <form onSubmit={submit} className="space-y-3">
            <TextField label="Current password" type="password" required autoComplete="current-password" value={form.currentPassword} onChange={(e) => setForm({ ...form, currentPassword: e.target.value })} />
            <TextField label="New password" type="password" required minLength={10} autoComplete="new-password" value={form.newPassword} onChange={(e) => setForm({ ...form, newPassword: e.target.value })} />
            <TextField label="Confirm new password" type="password" required autoComplete="new-password" value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} />
            {notice && <Alert kind={notice.kind}>{notice.text}</Alert>}
            <div className="flex justify-end">
              <Button type="submit" disabled={busy}>
                {busy ? 'Saving…' : 'Change password'}
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </>
  );
}
